import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MapPin, Phone, Facebook } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { useI18n } from "@/lib/I18nProvider";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const NAV_LINKS = [
    { label: t("nav.home"), to: "/" },
    { label: t("nav.services"), to: "/services" },
    { label: t("nav.industries"), to: "/industries" },
    { label: t("nav.solutions"), to: "/solutions" },
    { label: t("nav.stb"), to: "/set-top-box" },
    { label: t("nav.clients"), to: "/clients" },
    { label: t("nav.blog"), to: "/blog" },
    { label: t("nav.about"), to: "/about" },
    { label: t("nav.pricing"), to: "/pricing" },
    { label: t("nav.contact"), to: "/contact" },
  ];

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      toast.success(res.data.message || "Subscribed!");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Subscription failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="border-t border-white/10 bg-[#2A3B5C]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Brand & newsletter */}
          <div className="lg:col-span-5">
            <Logo size="lg" testId="footer-logo-link" />
            <p className="mt-6 text-slate-300 max-w-md leading-relaxed">
              {t("nav.home") === "Accueil"
                ? "Infrastructure OTT/IPTV de niveau opérateur. Lancez applis TV natives, mobiles, STB et streams live sur chaque plateforme — avec la monétisation et l'analytique pour grandir."
                : t("nav.home") === "Inicio"
                ? "Infraestructura OTT/IPTV de nivel operador. Lanza apps de TV nativas, móviles, STBs y emisiones en vivo en cada plataforma — con monetización y analítica para crecer."
                : t("nav.home") === "Início"
                ? "Infraestrutura OTT/IPTV de nível operador. Lance apps de TV nativas, mobile, STBs e transmissões ao vivo em cada plataforma — com monetização e analítica para crescer."
                : "Carrier-grade OTT/IPTV infrastructure. Launch native TV apps, mobile apps, set-top boxes and live streams across every major platform — with the monetization and analytics to grow them."}
            </p>

            <form onSubmit={subscribe} className="mt-8" data-testid="newsletter-form">
              <label className="form-label">{t("common.newsletter_label")}</label>
              <div className="flex gap-2 items-stretch">
                <input
                  data-testid="newsletter-email-input"
                  type="email"
                  required
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input flex-1"
                />
                <button
                  data-testid="newsletter-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="btn-primary !px-5"
                  aria-label="Subscribe"
                >
                  {loading ? "..." : <ArrowRight size={18} />}
                </button>
              </div>
              <p className="mt-2 text-xs text-slate-400">{t("common.no_spam")}</p>
            </form>

            {/* Social links */}
            <div className="mt-8 flex items-center gap-3" data-testid="footer-social">
              <a
                href="https://www.facebook.com/push2stream"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                data-testid="footer-social-facebook"
                className="w-10 h-10 rounded-full glass flex items-center justify-center text-slate-200 hover:text-white hover:border-[#1FA8FF] transition-colors"
              >
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Links */}
          <div className="lg:col-span-3 grid grid-cols-2 gap-8">
            <div>
              <div className="form-label !text-[#1FE0A8] !text-[10px]">{t("common.navigate")}</div>
              <ul className="mt-4 space-y-3">
                {NAV_LINKS.map((l, i) => (
                  <li key={i}>
                    <Link
                      to={l.to}
                      data-testid={`footer-link-${l.to.replace("/", "") || "home"}`}
                      className="text-slate-300 hover:text-white text-sm link-underline"
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="form-label !text-[#1FE0A8] !text-[10px]">{t("common.get_started")}</div>
              <ul className="mt-4 space-y-3">
                <li><Link to="/demo" data-testid="footer-link-demo" className="text-slate-300 hover:text-white text-sm link-underline">{t("nav.demo")}</Link></li>
                <li><Link to="/pricing" data-testid="footer-link-pricing" className="text-slate-300 hover:text-white text-sm link-underline">{t("nav.pricing")}</Link></li>
                <li><Link to="/contact" data-testid="footer-link-contact" className="text-slate-300 hover:text-white text-sm link-underline">{t("cta.contact_sales")}</Link></li>
              </ul>
            </div>
          </div>

          {/* Contact info */}
          <div className="lg:col-span-4">
            <div className="form-label !text-[#1FE0A8] !text-[10px]">{t("common.headquarters")}</div>
            <ul className="mt-4 space-y-4 text-sm text-slate-300">
              <li className="flex gap-3">
                <MapPin size={18} className="text-slate-400 mt-0.5 shrink-0" />
                <span>5850 Waterloo Rd<br/>Columbia, MD 21045 — USA</span>
              </li>
              <li className="flex gap-3">
                <Mail size={18} className="text-slate-400 mt-0.5 shrink-0" />
                <a href="mailto:sales@push2stream.com" className="hover:text-white">sales@push2stream.com</a>
              </li>
              <li className="flex gap-3">
                <Phone size={18} className="text-slate-400 mt-0.5 shrink-0" />
                <a href="tel:+12026561110" className="hover:text-white">+1 (202) 656-1110</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs font-mono text-slate-400 uppercase tracking-widest">
            © {new Date().getFullYear()} Push2Stream — Carrier Grade IPTV/OTT Solution
          </p>
          <p className="text-xs font-mono text-slate-400 uppercase tracking-widest flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#1FE0A8] pulse-dot"></span>
            {t("common.all_systems")}
          </p>
        </div>
      </div>
    </footer>
  );
}
